import React from 'react'

import {Box} from '@vio/atlas/helpers/Box'
import {BottomSheet} from '@vio/atlas/src/components/designSystem/BottomSheet'
import {PopUp} from '@vio/atlas/src/components/designSystem/PopUp'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'

import {PRICE_ALERT_PROMPT_SURFACE} from './constants'
import {PriceAlertPromptContent} from './PriceAlertPromptContent'
import {PriceAlertPromptFooter} from './PriceAlertPromptFooter'
import {hideBottomSheetClose, POPUP_WIDTH} from './styles'
import {usePriceAlertPromptCta} from './usePriceAlertPromptCta'

import type {ContentHotel, Hotel} from '@findhotel/sapi'

interface Props {
  hotel: Hotel | ContentHotel | undefined
  /** Close without analytics (after accept). */
  onClose: () => void
  /** Close and track the dismissal (Maybe later). */
  onDismiss: () => void
}

/**
 * The open prompt. It has no close (X) on either layout: "Maybe later", Esc and the
 * backdrop dismiss it. Both surfaces hide their built-in close, and the desktop popup
 * keeps `closeAction` so Esc and the backdrop still work.
 */
export const PriceAlertPromptSheet = ({hotel, onClose, onDismiss}: Props) => {
  const {isMobile} = useDeviceLayout()
  const {priceLabel, handleClick} = usePriceAlertPromptCta(hotel)

  const onAccept = (event: React.SyntheticEvent<Element, Event>) => {
    // handleClick opens the Price Watch / SUSI overlay synchronously, so closing in
    // the same pass never leaves the user with neither surface.
    handleClick(event)
    onClose()
  }

  const content = (
    <PriceAlertPromptContent
      hotelName={hotel?.hotelName ?? ''}
      priceLabel={priceLabel}
    />
  )

  const footer = (
    <PriceAlertPromptFooter onAccept={onAccept} onDismiss={onDismiss} />
  )

  if (isMobile) {
    return (
      <BottomSheet
        isOpen
        surfaceId={PRICE_ALERT_PROMPT_SURFACE}
        noPaddings
        onClose={onDismiss}
        // Hide the BottomSheet's hardcoded close (X); drag/backdrop still dismiss.
        css={hideBottomSheetClose}
        // Floating, title-less header so the illustration reaches the top edge.
        navBarProps={{
          variant: 'transparent',
          floating: true,
          centerContent: ''
        }}
        bottomBarContent={footer}
      >
        <Box
          paddingLeft="s500"
          paddingRight="s500"
          paddingTop="s500"
          paddingBottom="s500"
        >
          {content}
        </Box>
      </BottomSheet>
    )
  }

  return (
    <PopUp
      surfaceId={PRICE_ALERT_PROMPT_SURFACE}
      width={POPUP_WIDTH}
      closeOnEsc
      noPaddings
      isOpen
      closeAction={onDismiss}
      navBarProps={{
        variant: 'transparent',
        floating: true,
        centerContent: '',
        // Claiming the end slot suppresses PopUp's managed close button, while
        // closeAction stays so Esc and the backdrop still dismiss.
        endContent: ''
      }}
      footer={footer}
    >
      <Box
        paddingLeft="s500"
        paddingRight="s500"
        paddingTop="s500"
        paddingBottom="s500"
      >
        {content}
      </Box>
    </PopUp>
  )
}
